import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUserCircle } from 'react-icons/fa';
import { useRecoilState } from 'recoil';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Form,
  FormProvider,
  ValidatedTextArea,
} from '@tq/ui-components/dist/components/forms-engine';
import {
  AttentionMessage,
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBanner,
} from '@tq/ui-components';

import { useRequestExtension } from 'api';
import { MobileModal } from 'components';
import { BANNER_TIMEOUT } from 'config';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { showRequestExtensionState } from 'store';

type RequestExtensionModalProps = {
  consultationId: number;
};

export const RequestExtensionModal: React.FC<RequestExtensionModalProps> = ({
  consultationId,
}) => {
  const { t } = useTranslation('request_extension_modal');
  const isMobile = useIsMobile();
  const [showRequestExtension, setShowRequestExtension] = useRecoilState(
    showRequestExtensionState
  );

  const banner = useBanner();
  const { mutate: requestExtension, isLoading } = useRequestExtension();

  const handleClose = () => {
    setShowRequestExtension(false);
  };

  const handleRequestExtension = (reason: string) => {
    requestExtension(
      {
        consultationId,
        extensionReason: reason,
      },
      {
        onSuccess: () => {
          banner({
            id: 'request-extension-banner',
            title: t('extension_request_sent', 'Extension request has been sent'),
            duration: BANNER_TIMEOUT,
          });
          handleClose();
        },
        onError: () =>
          banner({
            id: 'request-extension-failed-banner',
            title: t('extension_request_failed', 'Extension request failed'),
            duration: BANNER_TIMEOUT,
            status: 'error',
          }),
      }
    );
  };

  const content = (
    <>
      <ModalHeader aria-label={t('request_extension_modal', 'Request extension modal')}>
        <Grid gridAutoFlow='column' gridGap={4}>
          <FaUserCircle color={COLOURS.blue[600]} />
          <span>{t('request_extension', 'Request extension')}</span>
        </Grid>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody overflow='auto'>
        <FormProvider mode='onChange'>
          <Form
            id='request-extension-modal-form'
            onValidSubmit={({ reason }) => handleRequestExtension(reason)}
          >
            <ValidatedTextArea
              data-testid='request-extension-reason'
              id='reason'
              label={t(
                'reason_text_label',
                'Please give the reason for requesting an extension'
              )}
              mode='onChange'
              validation={{
                required: {
                  value: true,
                  message: t('reason_text_required_error', 'Please enter reason'),
                },
                maxLength: {
                  value: 4000,
                  message: t(
                    'reason_text_length_error',
                    'You have exceeded the character limit'
                  ),
                },
              }}
            />
          </Form>
        </FormProvider>
        <AttentionMessage
          status='info'
          variant='inline'
          title={t(
            'information_message',
            'You will be notified of the response to this extension request'
          )}
          mt={8}
        />
      </ModalBody>
    </>
  );

  return isMobile ? (
    <MobileModal isOpen={showRequestExtension} onClose={handleClose}>
      {content}

      <ModalFooter>
        <Grid gridGap={4} w='100%'>
          <Button
            disabled={isLoading}
            isFullWidth
            type='submit'
            form='request-extension-modal-form'
          >
            {t('send_request_button_text', 'Send request')}
          </Button>
          <Button isFullWidth variant='tertiary' onClick={handleClose}>
            {t('cancel_button_text', 'Cancel')}
          </Button>
        </Grid>
      </ModalFooter>
    </MobileModal>
  ) : (
    <Modal isOpen={showRequestExtension} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        {content}

        <ModalFooter justifyContent='end'>
          <Grid gridAutoFlow='column' gridGap={4}>
            <Button variant='tertiary' onClick={handleClose}>
              {t('cancel_button_text', 'Cancel')}
            </Button>
            <Button disabled={isLoading} type='submit' form='request-extension-modal-form'>
              {t('send_request_button_text', 'Send request')}
            </Button>
          </Grid>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
